import { getRecoilStateValue } from "../../Orchestrator/Recoils/Events/Recoil";
import {
  AdmissionStatusEnum,
  PtModeEnum,
  isShuttleOffer,
} from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBooking, ILegTrain } from "../../Orchestrator/Types/Index";
import { shuttleFulfillmentIds, nolFulfillmentIds } from "./onwardRefund";
import type { IPaymentSessionRefundLeg } from "../../Orchestrator/Recoils/AddonsAtoms";
import type { IBookingResponse } from "../../Orchestrator/Types/Index";

/**
 * One DIRECTION (outbound / inbound) the passenger is moving, as the booking
 * describes it.
 *
 * A direction is a trip, and a trip can hold several train legs (a connecting
 * journey). Everything onward is anchored to the direction's SPAN — first
 * leg's origin to last leg's destination — because that is what the
 * onward-travel card is anchored to as well
 * (ManageSeatingAndAddons.tsx:85-106 merges the direction's legs exactly that
 * way). Nol, in contrast, is per-leg, so every train leg id is carried too.
 */
export interface IExchangedTrainDirection {
  /** Every train leg id on the exchanged trip, in trip order. */
  legIds: string[];
  /** First train leg's origin `stopPlaceRef`. */
  originRef: string;
  /** Last train leg's destination `stopPlaceRef`. */
  destinationRef: string;
  /**
   * Booking passenger ids on this direction's EXCHANGE_ONGOING admission(s) —
   * the passengers actually moving. Change-date is per-passenger: a party can
   * split, so this is NEVER "everyone on the trip". See buildSessionRefundLegs
   * for why this matters.
   */
  passengerIds: string[];
}

/**
 * Admission states meaning the passenger no longer holds the travel right.
 *
 * MIRRORED verbatim from `bookedShuttlesForLeg.ts:11-15`, not imported: that
 * module does not export the set, and it sits on the live cancel path which
 * this feature must not modify. The two lists must stay identical — a shuttle
 * this derivation considers live but that path considers history (or vice
 * versa) is a shuttle refunded twice or not at all.
 */
const TERMINAL_ADMISSION_STATUSES = new Set<string>([
  AdmissionStatusEnum.REFUNDED,
  AdmissionStatusEnum.RELEASED,
  AdmissionStatusEnum.EXCHANGED,
]);

/**
 * The train directions this booking is currently exchanging.
 *
 * An uncommitted exchange marks the ORIGINAL train offer's admissions
 * EXCHANGE_ONGOING and leaves its `tripCoverage.coveredTripId` pointing at the
 * ORIGINAL trip — whose stop places are the ones the shuttle was booked
 * against. That makes the booking itself the source of truth for "which
 * direction is moving", rather than the change-date screen's passenger-
 * selection atoms, which no page resets and which describe passengers rather
 * than trips.
 *
 * ALL train legs of the trip are collected, not just the first: a direction
 * can connect, and a Nol pass or a from-station shuttle attached past the
 * first leg would otherwise stay booked on the old date, unrefunded and
 * unmentioned.
 *
 * The leg filter mirrors trainLegsFromBooking (shuttleAddRequest.ts). That
 * function is deliberately NOT reused: it filters exchanged trips out, which
 * is the exact opposite of what this needs.
 */
export function exchangedTrainDirections(
  booking: IBooking | undefined,
): IExchangedTrainDirection[] {
  if (!booking) return [];

  // Per trip, the passenger ids of its EXCHANGE_ONGOING admission(s). Change-
  // date is per-passenger (ChangeDateMain.tsx, StartPreExchangeOperationAction
  // builds one exchange offer per selected passenger) — a party can split, so
  // this is never "everyone booked on the trip".
  const passengerIdsByTripId = new Map<string, Set<string>>();
  for (const offer of (booking.bookedOffers || []) as any[]) {
    const exchanging = (offer?.admissions || []).filter(
      (admission: any) => admission?.status === AdmissionStatusEnum.EXCHANGE_ONGOING,
    );
    if (exchanging.length === 0) continue;
    const tripId = offer?.tripCoverage?.coveredTripId;
    if (!tripId) continue;
    const passengerIds = passengerIdsByTripId.get(tripId) || new Set<string>();
    for (const admission of exchanging) {
      for (const passengerId of admission?.passengerIds || []) {
        if (passengerId) passengerIds.add(passengerId);
      }
    }
    passengerIdsByTripId.set(tripId, passengerIds);
  }
  if (passengerIdsByTripId.size === 0) return [];

  const directions: IExchangedTrainDirection[] = [];
  for (const trip of (booking.trips || []) as any[]) {
    const passengerIds = passengerIdsByTripId.get(trip?.id);
    if (!passengerIds) continue;
    // On the booking shape the mode lives at `timedLeg.service.mode.ptMode`;
    // the checkout atom shape the card reads uses `leg.trainType === "train"`
    // (ManageSeatingAndAddons.tsx:85). Same intent, two wire shapes.
    const trainLegs = (trip?.legs || []).filter(
      (candidate: any) => candidate?.timedLeg?.service?.mode?.ptMode === PtModeEnum.TRAIN,
    );
    const legIds = trainLegs.map((leg: any) => leg?.id).filter(Boolean) as string[];
    if (legIds.length === 0) continue;
    const first = trainLegs[0];
    const last = trainLegs[trainLegs.length - 1];
    directions.push({
      legIds,
      originRef: first?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef || "",
      destinationRef: last?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef || "",
      passengerIds: Array.from(passengerIds),
    });
  }
  return directions;
}

/**
 * True while the ACTIVE flow is a change of date or time.
 *
 * `isChangeDateStateAtom` alone is not enough: upgrade-to-premium sets it too
 * (UpgradeToPremiumAction.ts:209). An upgrade keeps the train's departure time,
 * so its shuttle stays valid and must never be refunded.
 */
export function isCdtExchange(): boolean {
  const isChangeDate = !!getRecoilStateValue<boolean>("isChangeDateStateAtom");
  const isUpgrade = !!getRecoilStateValue<boolean>("isUpgradeToPremiumStateAtom");
  return isChangeDate && !isUpgrade;
}

/**
 * True while the active flow is a change-date STARTED FOR THIS BOOKING.
 *
 * `isChangeDateStateAtom` is sticky — it is set at
 * StartPreExchangeOperationAction.ts:189 and there is no path that flips it
 * back on abandonment. A passenger who starts a change-date on BK1, abandons
 * at checkout (leaving the admissions EXCHANGE_ONGOING), then returns to BK1
 * from booking details to edit seating or add a shuttle would otherwise
 * satisfy every other condition here — flow flag true, exchange still pending,
 * same bookingId — and paying for the seat change would refund the shuttle.
 *
 * `quickExchangeBookingResponseAtom` closes that: it is written ONLY where an
 * exchange is created (StartPreExchangeOperationAction.ts:97 and the
 * quick-exchange / upgrade siblings) and is cleared by `clearBookingFlowAtoms`
 * on EVERY fresh flow entry — manage-seating, add-shuttle, cancel,
 * book-return, search.
 *
 * That clear is the WHOLE backstop. The atom is whitelisted for persistence
 * (WhitelistedAtoms.ts), because a change-date checkout has to survive the
 * `/checkout/cancel` reload — without persistence, cancelling on the MPGS page
 * and paying again omitted `refundLegs` and stranded the shuttle. So a refresh
 * no longer empties it, and anyone trimming `BOOKING_FLOW_ATOM_KEYS` re-arms
 * the abandoned-change-date refund this guard exists to prevent. The 30-minute
 * persistence TTL is the only other limit.
 *
 * `sessionStorage.exchangeBookingId` was considered and rejected: in the
 * scenario above it still reads "BK1", nothing removes it on entry to an
 * unrelated flow (only CancelQuickExchangeAction.ts:11 does), so it cannot
 * tell the two apart.
 */
function isCdtExchangeForBooking(bookingId: string | undefined): boolean {
  if (!bookingId || !isCdtExchange()) return false;
  const exchangeBooking = getRecoilStateValue<IBookingResponse>(
    "quickExchangeBookingResponseAtom",
  );
  return exchangeBooking?.booking?.id === bookingId;
}

/**
 * Will this change-date cancel the onward products attached to this train leg?
 *
 * ONE rule, two consumers: the payment payload and the onward-travel card's
 * notice. Reading it from the same place is what stops the card from promising
 * something the request then contradicts.
 *
 * MATCHED ON STOP REFS, NOT ON `legId`. The card's leg comes from
 * `selectedTrainLegsAtom` — the NEW search trip's legs (GetLegsFromTripAction
 * fills it with `legId: leg.id` off the selected search trip) — while the
 * exchanged directions come from the BOOKING's ORIGINAL trips. Those ids are
 * from different namespaces and never compare equal, so a legId match was dead
 * code in the real flow. A change-date keeps origin and destination and moves
 * only the date/time, so the origin/destination `stopPlaceRef` pair is stable
 * across both namespaces. On a round trip the two directions carry reversed
 * pairs, so they still distinguish.
 *
 * The card's leg spans the whole direction on a connecting journey
 * (ManageSeatingAndAddons.tsx:96-106 merges first-origin with last-
 * destination), which is why an exchanged direction is identified by its own
 * span rather than by any individual leg's refs.
 *
 * `getRecoilStateValue` is read here during render (via isCdtExchange), which
 * means a consuming component will NOT re-render if the underlying flow atoms
 * change after it has already rendered. Safe today because both
 * isChangeDateStateAtom and isUpgradeToPremiumStateAtom are written before
 * /checkout (and this card) mounts, never after.
 */
export function cdtCancelsOnwardForDirection(
  bookingResponse: IBookingResponse | undefined,
  trainLeg: ILegTrain | undefined,
): boolean {
  const originRef = trainLeg?.origin?.stopPlaceRef || "";
  const destinationRef = trainLeg?.destination?.stopPlaceRef || "";
  if (!originRef || !destinationRef) return false;
  const booking = bookingResponse?.booking;
  if (!isCdtExchangeForBooking(booking?.id)) return false;
  // Matching the direction is NOT enough to suppress the notice: the payload
  // applies four further gates the card cannot see (terminal statuses, the
  // session's already-refunded set, FULFILLED-and-in-`fulfillments`, and
  // per-passenger ownership). Asking the SAME question the payload asks — will
  // anything on this direction actually be refunded — is what stops the card
  // going quiet about a shuttle that then survives. It also keeps the notice
  // for a split party's non-moving passenger, whose shuttle is untouched.
  const refundedOfferIds = new Set(
    getRecoilStateValue<string[]>("refundedShuttleOfferIdsAtom") || [],
  );
  return exchangedTrainDirections(booking).some(
    (direction) =>
      direction.originRef === originRef &&
      direction.destinationRef === destinationRef &&
      refundIdsForDirection(bookingResponse, direction, refundedOfferIds).length > 0,
  );
}

/**
 * Will this change-date refund THIS PARTICULAR shuttle offer?
 *
 * Narrower than `cdtCancelsOnwardForDirection` on purpose, and the difference
 * is not academic. That predicate answers "does this direction lose ANYTHING",
 * and `refundIdsForDirection` counts Nol fulfillments toward it (see its second
 * loop) — so it goes true for a moving passenger whose NOL is refunded while
 * the direction's shuttle, owned entirely by non-moving passengers, survives on
 * its original date.
 *
 * Using the direction-level answer to hide the stale-date warning therefore
 * hid it on exactly the journey where it was still true, while the header
 * promised a refund of "the shuttle and Nol". The passenger was told the
 * opposite of what happens and lost the one prompt to fix it themselves.
 *
 * Mirrors the SHUTTLE half of `refundIdsForDirection` for a single offer: the
 * offer must belong to the matched direction, and ALL of its fulfillments must
 * be owned by exchanging passengers — a partly-refunded offer still strands a
 * ticket, so it does not count as gone.
 */
export function cdtRefundsShuttleOffer(
  bookingResponse: IBookingResponse | undefined,
  trainLeg: ILegTrain | undefined,
  shuttleOfferId: string | undefined,
): boolean {
  const originRef = trainLeg?.origin?.stopPlaceRef || "";
  const destinationRef = trainLeg?.destination?.stopPlaceRef || "";
  const booking = bookingResponse?.booking;
  if (!originRef || !destinationRef || !shuttleOfferId || !booking) return false;
  if (!isCdtExchangeForBooking(booking.id)) return false;

  const refundedOfferIds = new Set(
    getRecoilStateValue<string[]>("refundedShuttleOfferIdsAtom") || [],
  );
  return exchangedTrainDirections(booking).some((direction) => {
    if (!direction.originRef || !direction.destinationRef) return false;
    if (direction.originRef !== originRef || direction.destinationRef !== destinationRef) {
      return false;
    }
    const onThisDirection = directionShuttleOfferIds(
      booking,
      direction.originRef,
      direction.destinationRef,
      refundedOfferIds,
    ).includes(shuttleOfferId);
    if (!onThisDirection) return false;
    const exchangingPassengerIds = new Set(direction.passengerIds);
    const fulfillmentIds = shuttleFulfillmentIds(bookingResponse, shuttleOfferId);
    // EVERY, not some. A shuttle offer carries one admission per rider (see
    // buildSessionRefundLegs), so a party sharing one offer where only P1
    // changes date has only P1's fulfillment in `refundLegs` — P2's ticket
    // stays on the old date. Answering "yes, refunded" there would hide the
    // stale-date warning from the passenger it still applies to. Erring toward
    // SHOWING it is the safe side: imperfect advice beats silence about a
    // ticket that no longer matches the train.
    // `[].every()` is true, hence the length guard — an offer contributing no
    // fulfillments is refunding nothing.
    return (
      fulfillmentIds.length > 0 &&
      fulfillmentIds.every((id) =>
        ownedByExchangingPassenger(booking, id, exchangingPassengerIds),
      )
    );
  });
}

/**
 * The fulfillment ids ONE exchanged direction contributes to `refundLegs`.
 *
 * The single place that answers "what does this direction actually lose", read
 * by both the payload and the card's notice so the two cannot disagree.
 *
 * The empty-ref guard matters: a direction with no stop places would match any
 * shuttle offer whose trip also resolves to none ("" === ""), collecting an
 * offer from an unrelated direction. Needs malformed data on both sides, but
 * the cost of being wrong is a refund the passenger never agreed to.
 */
function refundIdsForDirection(
  bookingResponse: IBookingResponse | undefined,
  direction: IExchangedTrainDirection,
  refundedOfferIds: ReadonlySet<string>,
): string[] {
  const booking = bookingResponse?.booking;
  if (!booking) return [];
  if (!direction.originRef || !direction.destinationRef) return [];

  const exchangingPassengerIds = new Set(direction.passengerIds);
  const ids: string[] = [];

  for (const offerId of directionShuttleOfferIds(
    booking,
    direction.originRef,
    direction.destinationRef,
    refundedOfferIds,
  )) {
    // Only FULFILLED admissions present in booking.fulfillments come back, so
    // an unpaid or already-released shuttle contributes nothing.
    for (const id of shuttleFulfillmentIds(bookingResponse, offerId)) {
      if (ownedByExchangingPassenger(booking, id, exchangingPassengerIds)) ids.push(id);
    }
  }
  // Nol is per-LEG, not per-direction: a connecting journey can carry a pass on
  // the second leg only, so every train leg of the direction is queried.
  for (const legId of direction.legIds) {
    for (const id of nolFulfillmentIds(bookingResponse, legId)) {
      if (ownedByExchangingPassenger(booking, id, exchangingPassengerIds)) ids.push(id);
    }
  }
  return ids;
}

/**
 * Every live shuttle offer attached to ONE direction, by offer id.
 *
 * `bookedShuttlesForLeg` is deliberately NOT used here even though it answers
 * a near-identical question: it routes through `mapBookedShuttlesToSides`,
 * which keeps at most ONE offer per side (shuttleStops.ts:85-94, last-in-array
 * wins, by design — nothing in the payload can rank two offers). That is a
 * display ambiguity there and a money bug here: a party whose two shuttles
 * were added in separate sessions has two `toStation` offers, and refunding
 * only the surviving one strands the other passenger's shuttle silently. So
 * this enumerates ALL of them and lets `ownedByExchangingPassenger` do the
 * scoping. The filters below (product code, refunded-this-session, terminal
 * admissions, side matching) mirror bookedShuttlesForLeg.ts:44-64 and
 * shuttleStops.ts:77-79 exactly; both are on the live cancel path and must not
 * be modified, so they are mirrored rather than shared.
 */
function directionShuttleOfferIds(
  booking: IBooking,
  originRef: string,
  destinationRef: string,
  refundedOfferIds: ReadonlySet<string>,
): string[] {
  // Endpoint refs per trip, resolved the same way normalizeBookedShuttle does
  // (trip endpoints first, first/last leg as fallback).
  const endpointsByTripId = new Map<string, { originRef: string; destinationRef: string }>();
  for (const trip of (booking.trips || []) as any[]) {
    if (!trip?.id) continue;
    const legs = trip?.legs || [];
    endpointsByTripId.set(trip.id, {
      originRef:
        trip?.origin?.stopPlaceRef ||
        legs[0]?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef ||
        "",
      destinationRef:
        trip?.destination?.stopPlaceRef ||
        legs[legs.length - 1]?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef ||
        "",
    });
  }

  const offerIds: string[] = [];
  for (const offer of (booking.bookedOffers || []) as any[]) {
    const offerId = offer?.offerId || "";
    if (!offerId) continue;
    // Decided on the offer's ADMISSION product. A train offer carrying a
    // shuttle ANCILLARY_ITEM (SHTL-MBZ-REEM, "Shuttle From/To Reem Mall") read
    // as a shuttle offer here, which would have queued the TRAIN's own
    // admission into `refundLegs` on any booking whose trip endpoints happened
    // to line up. See isShuttleOffer.
    if (!isShuttleOffer(offer)) continue;
    if (refundedOfferIds.has(offerId)) continue;
    // An offer whose admissions are ALL terminal is history, not a booking.
    // No admissions at all proves nothing, so keep it.
    const admissions = offer?.admissions || [];
    if (
      admissions.length > 0 &&
      !admissions.some(
        (admission: any) => !TERMINAL_ADMISSION_STATUSES.has(admission?.status || ""),
      )
    ) {
      continue;
    }
    const endpoints = endpointsByTripId.get(offer?.tripCoverage?.coveredTripId || "");
    if (!endpoints) continue;
    const isToStation = endpoints.destinationRef === originRef;
    const isFromStation = endpoints.originRef === destinationRef;
    if (!isToStation && !isFromStation) continue;
    offerIds.push(offerId);
  }
  return offerIds;
}

/**
 * The SHUTTLE offers this change-date will refund, grouped by offer.
 *
 * `buildSessionRefundLegs` flattens shuttle and Nol fulfillments into one list
 * per booking, which is right for the payload (the backend just needs the ids)
 * and useless for a per-shuttle display. This keeps the grouping and drops Nol.
 *
 * Every gate the payload applies is applied here too — same
 * `exchangedTrainDirections`, same `directionShuttleOfferIds`, same
 * `shuttleFulfillmentIds`, same per-passenger ownership filter — because the
 * whole point is that the summary itemises exactly what the request refunds.
 * An offer contributing no owned fulfillments is dropped rather than shown at
 * zero: it is not being refunded.
 *
 * Gated on `isCdtExchangeForBooking` exactly like `buildSessionRefundLegs` — do
 * not add a second gate at the call site. The display must go quiet on the same
 * conditions the payload does (upgrade-to-premium, a non-CDT flow, an exchange
 * belonging to a different booking), and one shared gate is what guarantees it.
 */
export function cdtRefundedShuttleOffers(
  bookingResponse: IBookingResponse | undefined,
): { offerId: string; fulfillmentIds: string[]; stationRefs: string[] }[] {
  const booking = bookingResponse?.booking;
  if (!booking) return [];
  if (!isCdtExchangeForBooking(booking.id)) return [];

  const refundedOfferIds = new Set(
    getRecoilStateValue<string[]>("refundedShuttleOfferIdsAtom") || [],
  );

  const groups: { offerId: string; fulfillmentIds: string[]; stationRefs: string[] }[] = [];
  const seenOfferIds = new Set<string>();

  for (const direction of exchangedTrainDirections(booking)) {
    if (!direction.originRef || !direction.destinationRef) continue;
    const exchangingPassengerIds = new Set(direction.passengerIds);

    for (const offerId of directionShuttleOfferIds(
      booking,
      direction.originRef,
      direction.destinationRef,
      refundedOfferIds,
    )) {
      // A round trip can match the same offer from both directions; the payload
      // dedupes ids, so the display must not double-count the money.
      if (seenOfferIds.has(offerId)) continue;
      const fulfillmentIds = shuttleFulfillmentIds(bookingResponse, offerId).filter((id) =>
        ownedByExchangingPassenger(booking, id, exchangingPassengerIds),
      );
      if (fulfillmentIds.length === 0) continue;
      seenOfferIds.add(offerId);
      // The direction's TRAIN endpoints. A shuttle offer touches one of them at
      // one end; the other end is the shuttle's own stop, which is the only part
      // a display wants to name. Carried out because only this loop knows which
      // direction matched the offer.
      groups.push({
        offerId,
        fulfillmentIds,
        stationRefs: [direction.originRef, direction.destinationRef],
      });
    }
  }
  return groups;
}

/**
 * Passenger ids on the admission or ancillary this fulfillment id belongs to,
 * found by scanning every offer's admissions and ancillaries on the booking.
 * `shuttleFulfillmentIds`/`nolFulfillmentIds` return bare ids with no owner
 * attached, so this is the only way to answer "whose is it".
 *
 * Returns `[]` when the id cannot be found (or is found with no passengerIds)
 * — callers must treat that as "unknown owner", not "no one", and exclude it.
 */
function passengerIdsForFulfillmentId(booking: IBooking, id: string): string[] {
  for (const offer of (booking.bookedOffers || []) as any[]) {
    for (const admission of offer?.admissions || []) {
      if (admission?.id === id) return admission?.passengerIds || [];
    }
    for (const ancillary of offer?.ancillaries || []) {
      if (ancillary?.id === id) return ancillary?.passengerIds || [];
    }
  }
  return [];
}

/**
 * True when `id` is provably owned by at least one of the direction's
 * exchanging passengers. FAIL SAFE: an id whose owner cannot be determined
 * (not found on the booking, or found with no passengerIds) is EXCLUDED rather
 * than included — refunding something we cannot prove belongs to an exchanging
 * passenger is exactly the failure this check exists to prevent.
 */
function ownedByExchangingPassenger(
  booking: IBooking,
  id: string,
  exchangingPassengerIds: ReadonlySet<string>,
): boolean {
  const owners = passengerIdsForFulfillmentId(booking, id);
  if (owners.length === 0) return false;
  return owners.some((passengerId) => exchangingPassengerIds.has(passengerId));
}

/**
 * The `refundLegs` payload for POST /api/payment/session: the onward products
 * (shuttle admissions, Nol ancillaries) attached to the directions this
 * change-date is moving. The backend releases and refunds them when the
 * session settles, alongside the charge for the new train.
 *
 * Change-date is PER-PASSENGER: a party can split, so an exchanged direction's
 * shuttle offer can still carry a passenger who is NOT moving (a shuttle
 * offer holds one admission per rider). Only fulfillment ids whose owning
 * admission/ancillary names one of the direction's exchanging passengers are
 * kept — see `ownedByExchangingPassenger`.
 *
 * Pure read — no network, no atom writes, no throw path. A missing booking, a
 * booking that is not the one being paid for, a change-date started for a
 * DIFFERENT booking, or a direction with nothing attached each yield `[]`
 * rather than a partial payload: the backend acts on this irreversibly, so a
 * wrong-but-plausible list is the failure worth avoiding.
 *
 * One group is ever produced — the session is posted for a single bookingId.
 * The array shape is the backend's contract.
 */
export function buildSessionRefundLegs(
  bookingId: string | undefined,
): IPaymentSessionRefundLeg[] {
  if (!isCdtExchangeForBooking(bookingId)) return [];

  const bookingResponse = getRecoilStateValue<IBookingResponse>("bookingResponseAtom");
  const booking = bookingResponse?.booking;
  // The atom is global and outlives a flow; paying for booking A against
  // booking B's snapshot would refund a shuttle on an unrelated booking.
  if (!booking || booking.id !== bookingId) return [];

  // Offers already refunded in THIS session stay non-terminal on the booking
  // until it settles — without this they would be queued for a second refund.
  const refundedOfferIds = new Set(
    getRecoilStateValue<string[]>("refundedShuttleOfferIdsAtom") || [],
  );

  const fulfillmentIds = new Set<string>();
  for (const direction of exchangedTrainDirections(booking)) {
    for (const id of refundIdsForDirection(bookingResponse, direction, refundedOfferIds)) {
      fulfillmentIds.add(id);
    }
  }

  if (fulfillmentIds.size === 0) return [];
  return [{ bookingId: bookingId as string, fulfillmentIds: Array.from(fulfillmentIds) }];
}
